import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Navbar } from "@/components/Navbar";
import { TimerDisplay } from "@/components/TimerDisplay";
import { playSound } from "@/lib/sounds";
import { toast } from "sonner";
import { useTimerSocket } from "@/lib/useTimerSocket";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 2000;

export default function SharedTimer() {
  const { code } = useParams();
  const [timer, setTimer] = useState(null);
  const [err, setErr] = useState("");
  const [localRemaining, setLocalRemaining] = useState(0);
  const lastPhaseRef = useRef(null);
  const tickRef = useRef(null);

  const fetchTimer = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/shared/${code}`);
      setTimer(data);
      setLocalRemaining(data.remaining_seconds);
      if (lastPhaseRef.current && lastPhaseRef.current !== data.phase) {
        playSound(data.sound);
        toast.success(`Phase complete · now: ${data.phase.replace("_", " ")}`);
      }
      lastPhaseRef.current = data.phase;
    } catch (e) {
      if (e.response?.status === 404) setErr("Shared timer not found or no longer shared.");
      else setErr("Could not load shared timer.");
    }
  };

  useEffect(() => {
    fetchTimer();
    const i = setInterval(fetchTimer, POLL_MS);
    return () => clearInterval(i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  // Realtime push updates — instant sync with creator's controls
  useTimerSocket(code, (next) => {
    if (lastPhaseRef.current && lastPhaseRef.current !== next.phase) {
      playSound(next.sound);
      toast.success(`Phase complete · now: ${next.phase.replace("_", " ")}`);
    }
    lastPhaseRef.current = next.phase;
    setTimer(next);
    setLocalRemaining(next.remaining_seconds);
  });

  useEffect(() => {
    clearInterval(tickRef.current);
    if (timer?.is_running) {
      tickRef.current = setInterval(() => {
        setLocalRemaining((r) => (r > 0 ? r - 1 : 0));
      }, 1000);
    }
    return () => clearInterval(tickRef.current);
  }, [timer?.is_running, timer?.phase, timer?.id]);

  if (err) {
    return (
      <div className="min-h-screen bg-background bg-grid scanlines">
        <Navbar />
        <main className="mx-auto max-w-3xl px-6 py-24 text-center">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-destructive mb-3">// error</div>
          <h1 className="font-display font-bold text-3xl">{err}</h1>
          <div className="mt-6 font-mono text-sm text-muted-foreground">{">"} room code: {code}</div>
        </main>
      </div>
    );
  }

  if (!timer) {
    return (
      <div className="min-h-screen bg-background bg-grid scanlines">
        <Navbar />
        <main className="mx-auto max-w-3xl px-6 py-24 text-center font-mono text-sm text-muted-foreground">
          <span className="caret">// joining_room</span>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background bg-grid scanlines">
      <Navbar />
      <main className="mx-auto max-w-5xl px-6 py-12">
        <div className="section-label mb-3" data-testid="shared-room-label">shared_room · viewer</div>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight" data-testid="shared-timer-name">{timer.name}</h1>
            <p className="mt-2 font-mono text-sm text-muted-foreground">
              {">"} hosted by <span className="text-foreground">{timer.creator_name}</span> · viewer mode (read-only)
            </p>
          </div>
          <div className="border border-border p-4 font-mono text-xs">
            <div className="text-muted-foreground uppercase tracking-[0.3em] text-[10px] mb-1">// room_code</div>
            <div className="font-bold text-primary text-2xl tracking-widest" data-testid="shared-room-code">{timer.share_code}</div>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center">
          <TimerDisplay
            remaining={localRemaining}
            total={timer.total_seconds}
            phase={timer.phase}
            completedCycles={timer.completed_cycles}
            longBreakInterval={timer.long_break_interval}
          />

          <div className="mt-10 border border-border p-4 font-mono text-xs text-muted-foreground" data-testid="shared-viewer-note">
            {">"} only the timer creator can start, pause or reset this timer
          </div>
        </div>
      </main>
    </div>
  );
}
